/**
 * Auto-grid placement (TV1-1931 · S16).
 *
 * `x` / `y` / `width` / `height` are OPTIONAL on a layout widget (see
 * `UiWidgetsLayoutSchema`). A positionless widget flows into an auto-grid rather
 * than carrying hand-computed coordinates — which is what lets an LLM emit a
 * report layout without solving 2D no-overlap bin-packing.
 *
 * Placement resolves at RENDER TIME, not at write time:
 *
 * - `resolveLayout()` is a pure function called from a `useMemo` in
 *   `WidgetLayoutEditor` and `WidgetLayoutViewer`, and from `handleLayoutChange`
 *   in `WidgetLayoutComponent` before the validators run. Storage stays
 *   positionless until a human actually touches the layout.
 * - The first drag / resize / add / delete pushes the RESOLVED layout through
 *   the existing `onChange` → `ChangeUiWidgetLayout` path, so every widget gains
 *   concrete coordinates on the first edit ("normalize-all"). No mixed state
 *   ever survives an edit, and no new write path was needed.
 *
 * ⚠️ Resolve BEFORE render and BEFORE placement. With optional `x`/`y`, the old
 * `w.y! * rowPx` sites compile silently and yield `top: NaN`, and — worse — every
 * NaN comparison is `false`, so a positionless widget collides with nothing and
 * the non-overlap guarantee is quietly void. (That trap is why `placement.ts`
 * normalises a non-finite `y` rather than tolerating it; it is also why the
 * `noOverlapValidator` this module used to feed was retired in TV1-2291 · S10 —
 * a validator that REFUSES a save cannot resolve what compaction resolves.)
 *
 * Packing rule: **row-major wrap**. Widgets are placed left-to-right in
 * declaration order and wrap to a new row when they no longer fit in `cols`;
 * the new row starts at the bottom of the tallest widget in the row above. This
 * is deterministic and preserves the author's widget order as reading order.
 * (A skyline / first-fit packer would back-fill gutters, but it reorders the
 * report — widget 4 can land above widget 3.)
 *
 * Mixed layouts: a widget that already has `x` and `y` is PINNED and keeps its
 * coordinates. The auto-flow starts below the bottom of every pinned widget, so
 * a flowed widget can never collide with a pinned one — no occupancy map and no
 * collision scan required. (This is also what makes "add a widget to a
 * hand-arranged layout" behave: the new widget lands under the existing ones,
 * the arrangement is untouched.)
 *
 * TV1-2291 · §1 — the resolver is now MINIMUM-AWARE, but only when asked. A
 * second, optional argument carries the render-time geometry; with it, a widget
 * narrower than its type's legible minimum (`MIN_CONTENT_PX`) is widened to fit.
 * Without it nothing is clamped and the pre-TV1-2291 behaviour is byte-for-byte
 * intact. See `resolveLayout` for why the asymmetry runs that way round.
 */

import type { DeviceType, WidgetLayout } from './types';
import { minColsFor, MIN_CONTENT_PX } from './widgetSizing';
import type { Widget } from './Widgets/types';
import { defaultDimensions, type WidgetDimensions } from './Widgets/WidgetBuilder/WidgetConstructors';

/**
 * The grid the renderers have always used: 20 columns over 827px for web
 * (`cols` = 2 for mobile, set by `ReportsSidePanel`), square cells
 * (`rowPx === colPx`). A layout may override `cols` / `widthPx`; the schema has
 * carried both for a while.
 */
export const GRID_DEFAULTS = {
  cols: 20,
  widthPx: 827,
  device: 'web' as DeviceType
};

/**
 * Span used when `defaultDimensions` has no entry for a (device, type) pair.
 *
 * This is not defensive noise: `defaultDimensions.mobile` genuinely has no
 * `scatterChart` and no `criticalStock` entry, and the old
 * `defaultDimensions[layout.device!][widget.type]!` in `createWidget` crashed at
 * runtime on exactly those pairs. The resolver must never reproduce that.
 */
const FALLBACK_SPAN: Record<DeviceType, WidgetDimensions> = {
  web: { width: 8, height: 6 },
  mobile: { width: 1, height: 1 }
};

export type GridConfig = {
  cols: number;
  widthPx: number;
  device: DeviceType;
};

/**
 * The width the report is actually being drawn at. Handing this to
 * `resolveLayout` is what switches legibility clamping ON (TV1-2291 · §1).
 *
 * ⚠️ `scale` is REQUIRED, deliberately. `colPx` as `computeGridGeometry` reports
 * it already has the toolbar zoom multiplied in, and the minimum must be
 * computed against the 1x width — so the division cannot be optional without the
 * commonest mistake (forgetting it) being silent. Requiring the field also makes
 * `resolveLayout( layout, geometry )` — passing a whole `GridGeometry`, which
 * carries no `scale` — a compile error rather than a report that reflows while
 * the user zooms.
 */
export type DisplayGeometry = {
  /** Width of one column in CSS px, zoom included — `computeGridGeometry().colPx`. */
  colPx: number;
  /**
   * The report's gutter, in CSS px. Optional — a caller that omits it gets the
   * engine default, which is what every pre-2026-08-17 call site meant.
   *
   * It belongs in the DISPLAY geometry, not the grid: the gutter changes how wide a
   * card is DRAWN, and therefore how many columns it needs to stay legible, but it
   * changes nothing about the stored layout.
   */
  gapPx?: number;
  /** The toolbar zoom that `colPx` carries, divided back out before clamping. */
  scale: number;
};

/** A widget whose placement is fully concrete — safe to render and to validate. */
export type ResolvedWidget = Widget & {
  x: number;
  y: number;
  width: number;
  height: number;
};

// `Omit` first, then re-add: a plain `WidgetLayout & { widgets: ResolvedWidget[] }`
// intersects the two array types and TS resolves the element back to `Widget`
// (x/y optional again), which is exactly the NaN trap this module exists to kill.
export type ResolvedWidgetLayout = Omit<WidgetLayout, 'widgets'> & {
  widgets: ResolvedWidget[];
};

/**
 * Single source of truth for the grid config. The renderers and the packer MUST
 * agree on `cols` — if they drift, the packer wraps against one column count and
 * the renderer lays the pixels out against another.
 */
export const resolveGrid = ( layout: WidgetLayout ): GridConfig => ({
  cols: layout.cols || GRID_DEFAULTS.cols,
  widthPx: layout.widthPx || GRID_DEFAULTS.widthPx,
  device: layout.device || GRID_DEFAULTS.device
});

const clamp = ( value: number, min: number, max: number ): number =>
  Math.min( Math.max( value, min ), max );

const isPlaced = ( value: number | undefined | null ): value is number =>
  typeof value === 'number' && Number.isFinite( value );

/** A widget the author gave a concrete position — the auto-grid must not move it. */
type PinnedWidget = Widget & { x: number; y: number };

const isPinned = ( widget: Widget ): widget is PinnedWidget =>
  isPlaced( widget.x ) && isPlaced( widget.y );

/**
 * Columns this widget needs to stay legible at `display`, floored at 1.
 *
 * Returns 1 — "no minimum" — for the AUTHORED resolution, which is what keeps a
 * geometry-less `resolveLayout` identical to its pre-TV1-2291 self.
 */
const legibleMinCols = (
  widget: Widget,
  grid: GridConfig,
  display: DisplayGeometry | undefined
): number => {
  if ( !display ) return 1;

  const type = widget.definition?.type;

  // This is the same runtime hole `FALLBACK_SPAN` covers. `MIN_CONTENT_PX` is an
  // EXHAUSTIVE `Record<WidgetType, number>`, so the compiler guarantees every
  // union member has an entry — but a widget read back from storage can carry a
  // type that is no longer in the union, or no `definition` at all, and then the
  // lookup is `undefined`. `undefined / colPx` is NaN, NaN survives BOTH of
  // `minColsFor`'s clamps, and the widget resolves to `width: NaN` — precisely
  // the NaN trap this module exists to kill. Unknown type ⇒ impose no minimum.
  if ( !type || !( type in MIN_CONTENT_PX )) return 1;

  // ⚠️ UNSCALED (SDD K3b). Zoom magnifies an unchanged layout; computing the
  // minimum from the scaled width would make zooming OUT re-clamp and the report
  // rearrange itself mid-gesture. `widgetSizing.test.ts` pins what the wrong call
  // produces: 18 columns at 0.5x and 5 at 2x, for one chart at one container width.
  return minColsFor( type, display.colPx / display.scale, grid.cols, display.gapPx );
};

/**
 * The widget's span in grid units: its own `width`/`height` if given, else the
 * per-device default for its type, else the hard fallback. Always clamped into
 * the grid so `x + width <= cols` can hold for every widget.
 */
const spanFor = (
  widget: Widget,
  grid: GridConfig,
  display?: DisplayGeometry
): WidgetDimensions => {
  const fallback = FALLBACK_SPAN[grid.device] ?? FALLBACK_SPAN.web;
  const preset = defaultDimensions[grid.device]?.[widget.definition?.type];

  const width = isPlaced( widget.width ) ? widget.width : ( preset?.width ?? fallback.width );
  const height = isPlaced( widget.height ) ? widget.height : ( preset?.height ?? fallback.height );

  // The floor is the legible minimum instead of a bare 1. `minColsFor` already
  // returns a value inside `[1, cols]`, so the grid ceiling still wins — a widget
  // whose minimum exceeds the WHOLE grid lands at `cols` (full width) rather than
  // spilling off the canvas, and never at 0.
  return {
    width: clamp( Math.round( width ), legibleMinCols( widget, grid, display ), grid.cols ),
    height: Math.max( 1, Math.round( height ))
  };
};

/**
 * Resolve every widget in `layout` to a concrete `{ x, y, width, height }`.
 *
 * ```
 * resolveLayout( layout )                        // AUTHORED — widths pass through
 * resolveLayout( layout, { colPx, scale })       // DISPLAY  — clamped for legibility
 * ```
 *
 * Pure and idempotent in both modes: an already-resolved layout resolves to
 * itself, and resolving twice at the same geometry gives the same rectangles.
 *
 * ⚠️ THE ASYMMETRY IS LOAD-BEARING (SDD K3). The editor rebuilds the whole
 * layout from the resolved array on every mutation and persists it, so the two
 * resolutions are NOT interchangeable: mutating the clamped one writes every
 * OTHER widget's clamped width back as its authored width, and a drag on a narrow
 * viewport quietly destroys the desktop arrangement. Hence the default direction:
 *
 * - a RENDER path that forgets the geometry renders unclamped — visually wrong,
 *   harmless, and obvious on screen;
 * - a WRITE path that accidentally clamps CORRUPTS PERSISTED DATA, silently.
 *
 * The dangerous one is therefore the one that needs the explicit extra argument.
 * Callers that write (`WidgetLayoutComponent.handleLayoutChange`, the editor's
 * mutation base and its placement preview) must never pass it.
 */
export const resolveLayout = (
  layout: WidgetLayout,
  display?: DisplayGeometry
): ResolvedWidgetLayout => {
  const grid = resolveGrid( layout );
  const widgets = layout.widgets ?? [];

  if ( !widgets.length ) {
    return { ...layout, widgets: []};
  }

  // The auto-flow starts below every pinned widget. That single rule is what
  // guarantees a flowed widget can never overlap a pinned one.
  const flowStartY = widgets.reduce(( bottom, widget ) => {
    if ( !isPinned( widget )) return bottom;
    const { height } = spanFor( widget, grid, display );
    return Math.max( bottom, Math.max( 0, Math.round( widget.y )) + height );
  }, 0 );

  let cursorX = 0;
  let rowY = flowStartY;
  let rowBottom = flowStartY;

  const resolvedWidgets = widgets.map(( widget ): ResolvedWidget => {
    const { width, height } = spanFor( widget, grid, display );

    if ( isPinned( widget )) {
      const y = Math.max( 0, Math.round( widget.y ));
      // Clamp into the grid exactly the way `onDragStop` already does, so an
      // out-of-bounds authored `x` renders on-canvas instead of off it. This is
      // also the `x` re-clamp the minimum requires: `width` here is the WIDENED
      // width, so a widget clamped up against the right edge slides left to make
      // room instead of hanging off it (SDD §1: `x = min( storedX, cols - width )`).
      const x = clamp( Math.round( widget.x ), 0, Math.max( 0, grid.cols - width ));
      return { ...widget, x, y, width, height };
    }

    // Row-major wrap. `cursorX > 0` keeps a widget wider than the whole grid
    // (already clamped to `cols`) from wrapping onto an empty row forever.
    if ( cursorX > 0 && cursorX + width > grid.cols ) {
      rowY = rowBottom;
      cursorX = 0;
    }

    const placed: ResolvedWidget = { ...widget, x: cursorX, y: rowY, width, height };
    cursorX += width;
    rowBottom = Math.max( rowBottom, rowY + height );
    return placed;
  });

  return { ...layout, widgets: resolvedWidgets };
};
